/* eslint-disable react/jsx-max-depth */
import type { FC } from "react";
import { useCallback, useMemo, useState } from "react";

import { Check, Close } from "@similarweb/icons";
import type { MonitorAndProtectModelAdsApiModelAdTableRow } from "@similarweb/pro-api-client";
import type { ITableProps } from "@similarweb/pro-logic-components";
import { Table } from "@similarweb/pro-logic-components";
import { IconButton, Tooltip } from "@similarweb/ui-components";
import { Box, Flex, Text } from "@similarweb/ui-components/foundations";

import { getNoData } from "app/pages/digital-marketing/monitor-and-protect/shared/ui/table/get-no-data";
import { useTranslation } from "pro-features/components/WithTranslation";

import { mnpNavigation } from "../../app/navigation";
import { useMonitorAndProtectAdExcelDownload, useMonitorAndProtectAds } from "../../services/api/ads";
import { adsTracking } from "../../services/tracking/ads";
import { DEFAULT_MAX_EXCEL_ROWS } from "../../shared/data/component-config/component-config";
import { useMonitorAndProtectDataAndParams } from "../../shared/hooks/navigations";
import { Widget } from "../../shared/ui/layout/widget";

import { AdKeywordsTable } from "./components/ad-keywords-table";
import { AdsDataFilters } from "./filter-factory/filter-factory";
import { useDomainFilterQuickApply } from "./filter-factory/hooks";
import type { IExampleTableRow } from "./table-config";
import { createConfigBuilder, getParentRowBuilder } from "./table-config";

const AdsPage: FC = () => {
    const translate = useTranslation();
    const [{ pageSize, current }, setPaginationData] = useState<ITableProps["pagination"]>({
        pageSize: 100,
        current: 1,
    });

    const { sortBy, sortDir } = useMonitorAndProtectDataAndParams();
    const { toggleInclude, toggleExclude } = useDomainFilterQuickApply();
    const [expandedItems, setExpandedItems] = useState<MonitorAndProtectModelAdsApiModelAdTableRow["adId"][]>([]);
    const { data, isError, isLoading } = useMonitorAndProtectAds({
        page: current,
        pageSize: pageSize,
    });
    const { downloadExcel, isLoading: isLoadingExcel } = useMonitorAndProtectAdExcelDownload();

    const handleExpand = (itemIndex: MonitorAndProtectModelAdsApiModelAdTableRow) => {
        adsTracking.onRowExpand(itemIndex.adId);
        setExpandedItems((prev) => (prev[0] === itemIndex.adId ? [] : [itemIndex.adId]));
    };

    const handleSorting: ITableProps<IExampleTableRow>["onSort"] = (sorter) => {
        mnpNavigation.updateQueryParams({
            sortBy: sorter.field as string,
            sortDir: sorter.order,
        });
    };

    const dataFormatted = useMemo(() => {
        return getParentRowBuilder({
            translateFn: translate,
            onExpand: handleExpand,
            advertiserHoverActions: [
                {
                    label: (
                        <>
                            <Check size="xs" />
                            <span>{translate("monitor_and_protect.page.ads.table.domain.hover_action.include")}</span>
                        </>
                    ),
                    onClick: toggleInclude,
                },
                {
                    label: (
                        <>
                            <Close size="xs" />
                            <span>{translate("monitor_and_protect.page.ads.table.domain.hover_action.exclude")}</span>
                        </>
                    ),
                    onClick: toggleExclude,
                },
            ],
        }).transformData(data?.records ?? []);
    }, [data?.records, toggleInclude, toggleExclude, translate]);

    const tableConfig = useMemo(() => {
        return createConfigBuilder(translate, { current, pageSize }, data?.totalRecords)
            .setControlledSort(
                {
                    columnKey: sortBy,
                    order: sortDir,
                },
                handleSorting,
            )
            .setPagination({
                pageSize,
                current,
                total: data?.totalRecords ?? 0,
                onChange: (_currentPage: number, _pageSize: number, nextPage: number) => {
                    setPaginationData((prev) => ({ ...prev, current: nextPage }));
                },
            })
            .setExpandableConfig((builder) =>
                builder
                    .setExpandedRowRender((e) => (
                        <AdKeywordsTable
                            adItem={e}
                            onClose={() => setExpandedItems([])}
                        />
                    ))
                    .setRowExpandable((row) => {
                        return row.keywordsCount.data > 0;
                    })
                    .setShowExpandColumn(false)
                    .setExpandedRowKeys(expandedItems),
            )
            .build();
    }, [current, data?.totalRecords, expandedItems, pageSize, sortBy, sortDir, translate]);

    const onExcelClick = useCallback(() => {
        adsTracking.onExcelDownload();
        void downloadExcel()
            .then(() => {
                return {
                    success: true,
                    message: "",
                };
            })
            .catch((error: Error) => {
                return {
                    success: false,
                    message: error.message || "An error occurred while downloading the Excel file.",
                };
            });
    }, [downloadExcel]);

    return (
        <Flex
            flexDirection="column"
            gap="24px"
            data-automation="Ads-page-container"
        >
            <Widget title={translate("monitor_and_protect.page.ads.title")}>
                <Widget.Header>
                    <Flex
                        alignItems="center"
                        justifyContent="space-between"
                        flex="1 1 auto"
                    >
                        <Text fontVariant="h5_2">{translate("monitor_and_protect.page.ads.title")}</Text>
                        <Box display={dataFormatted?.length > 0 ? undefined : "none"}>
                            <Tooltip
                                content={translate("monitor_and_protect.features.ads_report.excel_download_tooltip", {
                                    maxRows: DEFAULT_MAX_EXCEL_ROWS.toLocaleString(),
                                })}
                                placement="top"
                            >
                                <IconButton
                                    iconName="excel"
                                    isLoading={isLoadingExcel}
                                    type="flat"
                                    iconSize="sm"
                                    onClick={onExcelClick}
                                />
                            </Tooltip>
                        </Box>
                    </Flex>
                </Widget.Header>
                <AdsDataFilters />
                <Table
                    selection={{
                        enabled: false,
                    }}
                    isLoading={isLoading}
                    noData={getNoData(isError, "ads", translate)}
                    sticky={{
                        offsetHeader: -32,
                    }}
                    onSort={tableConfig.onSort}
                    rowKey="id"
                    expandable={tableConfig.expandable}
                    pagination={tableConfig.pagination}
                    columnConfig={tableConfig.columns}
                    data={dataFormatted}
                />
            </Widget>
        </Flex>
    );
};

export { AdsPage };
